"use client";

import React from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import Chip from "@/components/global/ui/Chip";
import { CTAButton } from "@/shadcn/ui/button";
import { cn } from "@/lib/utils";
import type { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import orangebg from "@/public/home/orange-bg-2.png";
import Image from "next/image";
import logo from "@/public/logo-silhouette.svg";
import cardBG from "@/public/home/orange-bg-pricing.png";
import pricingBG from "@/public/home/pricing-mobile-bg.png";

type BillingCycle = "monthly" | "yearly";

export default function PricingSection({ data }: { data: PageHomeSchema }) {
  const {
    pricingPre,
    pricingChip,
    pricingTitle,
    pricingMonthlyPrice,
    pricingYearlyPrice,
    pricingCards,
    pricingCTA,
  } = data;

  const [billingCycle, setBillingCycle] =
    React.useState<BillingCycle>("yearly");
  const priceRefDesktop = React.useRef<HTMLDivElement>(null);
  const cardsRefDesktop = React.useRef<HTMLDivElement>(null);

  // Refs + state for clip-path notch cutout via inline SVG
  const notchRowRef = React.useRef<HTMLDivElement>(null);
  const cardWrapperRef = React.useRef<HTMLDivElement>(null);
  const [notchClipId] = React.useState(
    () => `notch-clip-${Math.random().toString(36).slice(2, 9)}`,
  );
  const [notchPathD, setNotchPathD] = React.useState<string>("");
  // Pixel-based outline path for the border SVG overlay
  const [borderPathD, setBorderPathD] = React.useState<string>("");
  const [cardSize, setCardSize] = React.useState({ w: 0, h: 0 });

  React.useEffect(() => {
    const compute = () => {
      const notchEl = notchRowRef.current;
      const cardEl = cardWrapperRef.current;
      if (!notchEl || !cardEl) return;

      const cardRect = cardEl.getBoundingClientRect();
      const notchRect = notchEl.getBoundingClientRect();

      const W = cardRect.width;
      const H = cardRect.height;
      if (W === 0 || H === 0) return;

      // Normalized 0-1 for objectBoundingBox clip-path
      const cy = (notchRect.top - cardRect.top + notchRect.height / 2) / H;
      const rx = notchRect.height / 2 / W;
      const ry = notchRect.height / 2 / H;

      const clipD = [
        `M 0 0 L 1 0 L 1 1 L 0 1 Z`,
        `M 0 ${cy - ry} A ${rx} ${ry} 0 0 1 0 ${cy + ry} Z`,
        `M 1 ${cy - ry} A ${rx} ${ry} 0 0 0 1 ${cy + ry} Z`,
      ].join(" ");

      setNotchPathD(clipD);

      // Pixel-based path for the visible border stroke
      const pxCy = notchRect.top - cardRect.top + notchRect.height / 2;
      const r = notchRect.height / 2;

      // Single continuous outline: top edge → right edge down to notch → right semicircle → right edge down to bottom → bottom edge → left edge up to notch → left semicircle → left edge up to top
      const borderD = [
        `M 0 0`,
        `L ${W} 0`,
        `L ${W} ${pxCy - r}`,
        `A ${r} ${r} 0 0 0 ${W} ${pxCy + r}`,
        `L ${W} ${H}`,
        `L 0 ${H}`,
        `L 0 ${pxCy + r}`,
        `A ${r} ${r} 0 0 0 0 ${pxCy - r}`,
        `Z`,
      ].join(" ");

      setBorderPathD(borderD);
      setCardSize({ w: W, h: H });
    };

    const raf = requestAnimationFrame(compute);

    const observer = new ResizeObserver(compute);
    if (cardWrapperRef.current) observer.observe(cardWrapperRef.current);

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activePrice =
    billingCycle === "monthly" ? pricingMonthlyPrice : pricingYearlyPrice;

  // Track whether we're on lg+ screens for responsive animation values
  const [isLg, setIsLg] = React.useState(false);
  React.useEffect(() => {
    const mql = window.matchMedia("(min-width: 1024px)");
    setIsLg(mql.matches);
    const handler = (e: MediaQueryListEvent) => setIsLg(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  // Scroll-driven animation tracked against the price container
  // Starts well before the element enters viewport (offset -1 = one full element height above viewport bottom)
  const { scrollYProgress } = useScroll({
    target: priceRefDesktop,
    offset: ["-1 1", "0 0.8"],
  });

  // translateY: -210% → 0% as the price element scrolls into view
  const priceYRaw = useTransform(scrollYProgress, [0, 1], [-210, 0]);
  const priceYSpring = useSpring(priceYRaw, {
    stiffness: 120,
    damping: 30,
    mass: 0.8,
  });
  const priceY = useTransform(priceYSpring, (v) => `${v}%`);

  // scale: 1 → 8/47 on lg (base font 47rem shrinks to 8rem), no scale change on mobile
  const priceScaleRaw = useTransform(
    scrollYProgress,
    [0, 1],
    [1, isLg ? 8 / 47 : 1],
  );
  const priceScale = useSpring(priceScaleRaw, {
    stiffness: 120,
    damping: 30,
    mass: 0.8,
  });

  // textShadow: fades out as the text shrinks
  const priceShadowOpacityRaw = useTransform(scrollYProgress, [0, 1], [0.5, 0]);
  const priceShadowOpacity = useSpring(priceShadowOpacityRaw, {
    stiffness: 120,
    damping: 30,
    mass: 0.8,
  });
  const priceTextShadow = useTransform(
    priceShadowOpacity,
    (v) => `0 0 64px rgba(255, 255, 255, ${v})`,
  );

  // --- Pricing card scroll-driven animation ---
  const { scrollYProgress: cardScrollProgress } = useScroll({
    target: cardsRefDesktop,
    offset: ["start end", "center center"],
  });

  const cardSpringConfig = { stiffness: 80, damping: 24, mass: 0.6 };

  // Main card: slides up from 120px → 0
  const mainCardYRaw = useTransform(cardScrollProgress, [0, 0.6], [120, 0]);
  const mainCardY = useSpring(mainCardYRaw, cardSpringConfig);

  // Ghost cards: appear after main card lands, then slide outward + scale down
  // Left ghost
  const ghostLeftXRaw = useTransform(
    cardScrollProgress,
    [0.45, 1],
    [0, isLg ? -480 : -60],
  );
  const ghostLeftX = useSpring(ghostLeftXRaw, cardSpringConfig);
  // Right ghost
  const ghostRightXRaw = useTransform(
    cardScrollProgress,
    [0.45, 1],
    [0, isLg ? 480 : 60],
  );
  const ghostRightX = useSpring(ghostRightXRaw, cardSpringConfig);
  // Shared ghost values
  const ghostScaleRaw = useTransform(cardScrollProgress, [0.45, 1], [1, 0.9]);
  const ghostScale = useSpring(ghostScaleRaw, cardSpringConfig);
  const ghostOpacityRaw = useTransform(
    cardScrollProgress,
    [0.4, 0.55],
    [0, 0.4],
  );
  const ghostOpacity = useSpring(ghostOpacityRaw, cardSpringConfig);

  return (
    <React.Fragment>
      {/* DESKTOP */}
      <section
        className="hidden relative lg:block bg-[#040404] pb-[12rem]"
        id="pricing"
      >
        <div className="w-full h-[10%] bg-gradient-to-b from-white to-transparent top-0 z-[9]  absolute" />
        <div className="absolute top-0 w-full h-fit">
          <Image
            src={orangebg}
            quality={100}
            className="relative w-full z-[1]"
            alt=""
          />
          <div className="w-full h-[10%] bg-gradient-to-b from-white to-transparent top-0 z-[9]  absolute" />

          <div className="w-full h-[10%] bg-gradient-to-t from-[#040404] to-transparent bottom-0 z-[5]  absolute" />
        </div>
        <div className="pointer-events-none absolute inset-0 z-[2] bg-[url('/noise.svg?v=4')] bg-repeat opacity-50 [mask-image:linear-gradient(to_top,black_50%,transparent_95%)]" />

        <div className="flex relative flex-col gap-[4rem] pt-[45.875rem] z-[9]">
          {pricingPre && (
            <div className="mx-auto bg-white w-fit py-[.5rem] px-[1rem]">
              <p className="uppercase m-0 text-[2.25rem] leading-[110%] font-spacemono text-[#040404]">
                {pricingPre}
              </p>
            </div>
          )}

          <div className="relative pt-[72rem] z-[99]">
            {pricingChip && (
              <div className="w-fit [&_*]:text-white mx-auto mb-[4rem]">
                <Chip
                  text={pricingChip}
                  animatedIcon
                  spinnerSize={32}
                  spinnerVariant="arrow-spin"
                />
              </div>
            )}

            {pricingTitle && (
              <div className="richtext prose-h1:text-white">
                <PortableText
                  value={pricingTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          <div
            ref={priceRefDesktop}
            className="flex relative flex-col items-center mx-auto gap-[3rem] h-[31.625rem] w-[min(51.875rem,100%)]"
          >
            {/* Billing toggle */}
            <div className="flex items-center gap-[1.5rem]">
              <span
                className={cn(
                  "text-[2.25rem] leading-none transition-colors",
                  billingCycle === "monthly" ? "text-white" : "text-white/40",
                )}
              >
                Billed Monthly
              </span>

              <button
                type="button"
                role="switch"
                aria-checked={billingCycle === "yearly"}
                aria-label="Toggle billing cycle"
                onClick={() =>
                  setBillingCycle((prev) =>
                    prev === "monthly" ? "yearly" : "monthly",
                  )
                }
                className={cn(
                  "relative w-[7rem] h-[3.75rem] rounded-full cursor-pointer transition-colors duration-300 shrink-0 border overflow-clip",
                  billingCycle === "yearly" ? "bg-[#FF5500]" : "bg-[#E2E2E2]",
                )}
              >
                <div
                  className={`${billingCycle === "yearly" ? "bg-white " : "bg-black"} absolute top-[-2rem] w-full h-[1.5rem] blur-[2.5rem]`}
                />
                <div className="absolute top-1/2 rounded-full -translate-y-1/2 border-r-[.25rem] border-r-white h-[1.375rem] left-[1.75rem] w-[.125rem]" />
                <span
                  className={cn(
                    "absolute top-1/2 -translate-y-1/2 w-[2.75rem] h-[2.75rem] rounded-full bg-white transition-all duration-300",
                    billingCycle === "yearly"
                      ? "left-[calc(100%-3.25rem)]"
                      : "left-[0.5rem]",
                  )}
                  style={{ boxShadow: "0 4px 9px 0 rgba(0, 0, 0, 0.25)" }}
                />
                <div className="absolute top-1/2 rounded-full border border-white -translate-y-1/2 h-[1.375rem] right-[1.25rem] w-[1.375rem]" />
              </button>

              <span
                className={cn(
                  "text-[2.25rem] leading-none transition-colors",
                  billingCycle === "yearly" ? "text-white" : "text-white/40",
                )}
              >
                Billed Yearly
              </span>
            </div>

            {/* Price display */}
            {activePrice && (
              <div className="flex relative flex-col items-center gap-[1.5rem]">
                {activePrice.price && (
                  <motion.span
                    className="font-normal leading-[110%] tracking-[-0.04em] text-center absolute will-change-transform text-[4rem] lg:text-[47rem]"
                    style={{
                      fontFamily: '"Helvetica Neue", sans-serif',
                      textShadow: priceTextShadow,
                      WebkitTextStrokeWidth: "1px",
                      WebkitTextStrokeColor: "#fff",
                      background:
                        "linear-gradient(180deg, #FFF 0%, #FFB19B 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      y: priceY,
                      scale: priceScale,
                      transformOrigin: "center top",
                    }}
                  >
                    {activePrice.price}
                  </motion.span>
                )}

                {billingCycle === "yearly" && activePrice.discounted && (
                  <span className="leading-none line-through text-[4rem] text-white/60 pt-[11.75rem] tracking-[-1.28px] lg:text-[3rem]">
                    {activePrice.discounted}
                  </span>
                )}

                {billingCycle === "monthly" && activePrice.discounted && (
                  <span className="opacity-0 leading-none line-through text-[4rem] text-white/60 pt-[11.75rem] tracking-[-1.28px] lg:text-[3rem]">
                    ph
                  </span>
                )}
              </div>
            )}

            {pricingCTA && (
              <div className="">
                <CTAButton data={pricingCTA} />
              </div>
            )}
          </div>

          {pricingCards && pricingCards.length > 0 && (
            <div ref={cardsRefDesktop} className="mx-auto gap-[2rem] px-[2rem]">
              {pricingCards.map((card, index) => {
                const cardContent = (
                  <>
                    <Image
                      className="absolute inset-0 w-full h-full z-[1]"
                      src={cardBG}
                      alt=""
                    />
                    <div className="relative w-full h-full z-[5]">
                      <div className="flex justify-between w-full border-b p-[2rem] border-b-white/20">
                        <h3 className="text-white text-[2.25rem] leading-[110%] uppercase m-0 font-spacemono">
                          {card.title}
                        </h3>

                        {card.subTitle && (
                          <p className="text-white text-[2.25rem] leading-[110%] uppercase font-spacemono m-0">
                            {card.subTitle}
                          </p>
                        )}
                      </div>

                      <div className="px-[4rem] mb-[4rem]">
                        <p className="m-0 text-black text-[10rem]">
                          TradeStats
                        </p>
                        <Image
                          src={logo}
                          alt="TradeStats"
                          className="mx-auto"
                        />
                      </div>

                      <div
                        ref={notchRowRef}
                        className="relative w-full h-[4rem] mb-[2rem]"
                      >
                        <hr className="absolute top-1/2 w-[calc(100%+4rem)] -left-[2rem] border-t-[2px] border-dashed -translate-y-1/2 border-t-[#040404]" />
                      </div>

                      {card.checks && card.checks.length > 0 && (
                        <div className="flex flex-col mx-auto gap-[1.25rem] px-[4rem] pb-[5rem]">
                          {card.checks.map((check, checkIndex) => (
                            <div
                              key={`check-${checkIndex}`}
                              className="flex items-center gap-[1rem]"
                            >
                              <svg
                                width="9"
                                height="15"
                                viewBox="0 0 9 15"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect
                                  x="3"
                                  y="3"
                                  width="3"
                                  height="3"
                                  fill="black"
                                />
                                <rect
                                  x="6"
                                  y="6"
                                  width="3"
                                  height="3"
                                  fill="black"
                                />
                                <rect y="6" width="3" height="3" fill="black" />
                                <rect
                                  x="3"
                                  y="9"
                                  width="3"
                                  height="3"
                                  fill="black"
                                />
                              </svg>
                              <span className="text-[2.25rem] leading-[150%] text-black">
                                {check.text}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                );

                const clipStyle = notchPathD
                  ? { clipPath: `url(#${notchClipId})` }
                  : undefined;

                return (
                  <div
                    key={`card-${index}`}
                    className="relative flex items-center justify-center w-[min(59.875rem,100%)] mx-auto overflow-visible"
                  >
                    {/* Inline SVG clipPath definition */}
                    {notchPathD && (
                      <svg width="0" height="0" className="absolute">
                        <defs>
                          <clipPath
                            id={notchClipId}
                            clipPathUnits="objectBoundingBox"
                          >
                            <path d={notchPathD} clipRule="evenodd" />
                          </clipPath>
                        </defs>
                      </svg>
                    )}

                    {/* Left ghost card */}
                    <motion.div
                      aria-hidden
                      className={cn(
                        "flex absolute top-0 left-0 flex-col w-full origin-center pointer-events-none gap-[2rem] rounded-[0.5rem]",
                        !notchPathD && "overflow-clip",
                      )}
                      style={{
                        x: ghostLeftX,
                        scale: ghostScale,
                        opacity: ghostOpacity,
                        ...clipStyle,
                      }}
                    >
                      {cardContent}
                    </motion.div>

                    {/* Right ghost card */}
                    <motion.div
                      aria-hidden
                      className={cn(
                        "flex absolute top-0 left-0 flex-col w-full origin-center pointer-events-none gap-[2rem] rounded-[0.5rem]",
                        !notchPathD && "overflow-clip",
                      )}
                      style={{
                        x: ghostRightX,
                        scale: ghostScale,
                        opacity: ghostOpacity,
                        ...clipStyle,
                      }}
                    >
                      {cardContent}
                    </motion.div>

                    {/* Main card — slides up */}
                    <motion.div
                      ref={cardWrapperRef}
                      className={cn(
                        "flex relative flex-col w-full gap-[2rem] z-[2]",
                        !notchPathD && "overflow-clip",
                      )}
                      style={{
                        y: mainCardY,
                        ...clipStyle,
                      }}
                    >
                      {cardContent}
                    </motion.div>

                    {/* Border that follows the clip-path shape */}
                    {borderPathD && cardSize.w > 0 && (
                      <motion.svg
                        className="absolute top-0 left-0 pointer-events-none z-[3]"
                        width={cardSize.w}
                        height={cardSize.h}
                        viewBox={`0 0 ${cardSize.w} ${cardSize.h}`}
                        fill="none"
                        style={{
                          y: mainCardY,
                        }}
                      >
                        <path
                          d={borderPathD}
                          stroke="rgba(255,255,255,0.2)"
                          strokeWidth="1"
                          fill="none"
                        />
                      </motion.svg>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* MOBILE */}
      <section className="relative lg:hidden bg-[#040404]" id="pricing">
        <Image
          src={pricingBG}
          alt=""
          className="absolute w-full h-full z-[1]"
        />
        <div className="pointer-events-none absolute inset-0 z-[2] bg-[url('/noise.svg?v=4')] bg-repeat opacity-50 [mask-image:linear-gradient(to_top,black_50%,transparent_95%)]" />
        {/* <div className="w-full h-[10%] bg-gradient-to-b from-white to-transparent top-0 z-[9]  absolute" /> */}

        <div className="flex relative flex-col gap-[4rem] pt-[6rem] z-[9]">
          <div className="relative">
            {pricingChip && (
              <div className="w-fit [&_*]:text-white mx-auto mb-[4rem]">
                <Chip
                  text={pricingChip}
                  animatedIcon
                  spinnerSize={32}
                  spinnerVariant="arrow-spin"
                />
              </div>
            )}

            {pricingTitle && (
              <div className="richtext prose-h1:text-white">
                <PortableText
                  value={pricingTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          <div className="flex relative flex-col items-center mx-auto gap-[3rem] h-[35.625rem] w-[min(51.875rem,100%)]">
            {/* Billing toggle */}
            <div className="flex items-center gap-[1.5rem]">
              <span
                className={cn(
                  "text-[2.25rem] leading-none transition-colors",
                  billingCycle === "monthly" ? "text-white" : "text-white/40",
                )}
              >
                Billed Monthly
              </span>

              <button
                type="button"
                role="switch"
                aria-checked={billingCycle === "yearly"}
                aria-label="Toggle billing cycle"
                onClick={() =>
                  setBillingCycle((prev) =>
                    prev === "monthly" ? "yearly" : "monthly",
                  )
                }
                className={cn(
                  "relative w-[7rem] h-[3.75rem] rounded-full cursor-pointer transition-colors duration-300 shrink-0 border",
                  billingCycle === "yearly" ? "bg-[#FF5500]" : "bg-[#E2E2E2]",
                )}
              >
                <div className="absolute top-1/2 rounded-full -translate-y-1/2 border-r-[.25rem] border-r-white h-[1.375rem] left-[1.75rem] w-[.125rem]" />
                <span
                  className={cn(
                    "absolute top-1/2 -translate-y-1/2 w-[2.75rem] h-[2.75rem] rounded-full bg-white transition-all duration-300",
                    billingCycle === "yearly"
                      ? "left-[calc(100%-3.25rem)]"
                      : "left-[0.5rem]",
                  )}
                  style={{ boxShadow: "0 4px 9px 0 rgba(0, 0, 0, 0.25)" }}
                />
                <div className="absolute top-1/2 rounded-full border border-white -translate-y-1/2 h-[1.375rem] right-[1.25rem] w-[1.375rem]" />
              </button>

              <span
                className={cn(
                  "text-[2.25rem] leading-none transition-colors",
                  billingCycle === "yearly" ? "text-white" : "text-white/40",
                )}
              >
                Billed Yearly
              </span>
            </div>

            {/* Price display */}
            {activePrice && (
              <div className="flex relative flex-col items-center gap-[1.5rem] w-full px-[2rem]">
                {activePrice.price && (
                  <span
                    className="font-normal leading-[110%] tracking-[-0.04em] text-center will-change-transform text-[12rem]"
                    style={{
                      fontFamily: '"Helvetica Neue", sans-serif',
                      WebkitTextStrokeWidth: "1px",
                      WebkitTextStrokeColor: "#fff",
                      background:
                        "linear-gradient(180deg, #FFF 0%, #FFB19B 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    {activePrice.price}
                  </span>
                )}

                {billingCycle === "yearly" && activePrice.discounted && (
                  <span className="leading-none line-through text-[4rem] text-white/60 tracking-[-1.28px]">
                    {activePrice.discounted}
                  </span>
                )}

                {pricingCTA && (
                  <div className="w-full">
                    <CTAButton data={pricingCTA} />
                  </div>
                )}
              </div>
            )}
          </div>

          {pricingCards && pricingCards.length > 0 && (
            <div className="mx-auto gap-[2rem] px-[2rem]">
              {pricingCards.map((card, index) => {
                const cardContent = (
                  <div className="relative w-full h-full z-[5]">
                    {card.checks && card.checks.length > 0 && (
                      <div className="flex flex-col gap-[1.25rem] px-[2rem] pb-[5rem]">
                        {card.checks.map((check, checkIndex) => (
                          <div
                            key={`check-${checkIndex}`}
                            className="flex gap-[1rem]"
                          >
                            <svg
                              width="9"
                              height="15"
                              viewBox="0 0 9 15"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <rect
                                x="3"
                                y="3"
                                width="3"
                                height="3"
                                fill="white"
                              />
                              <rect
                                x="6"
                                y="6"
                                width="3"
                                height="3"
                                fill="white"
                              />
                              <rect y="6" width="3" height="3" fill="white" />
                              <rect
                                x="3"
                                y="9"
                                width="3"
                                height="3"
                                fill="white"
                              />
                            </svg>
                            <span className="text-[2.25rem] leading-[150%] text-white">
                              {check.text}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );

                return (
                  <div
                    key={`card-${index}`}
                    className="relative flex items-center justify-center w-[min(59.875rem,100%)] mx-auto overflow-visible"
                  >
                    <div className="flex relative flex-col w-full gap-[2rem] overflow-clip z-[2]">
                      {cardContent}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </React.Fragment>
  );
}
